import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Paper,
  FormLabel,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import {
  golfTooltipHeaders,
  originsTooltip,
} from "../../../constants/TooltipInformation";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const CategoryGroups = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [categoryGroupCode, setCategoryGroupCode] = useState<string | null>(
    null,
  );
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [CategoryGroups, setCategoryGroups] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  useEffect(() => {
    fieldValues && setCategoryGroups(fieldValues);
  }, []);

  useEffect(() => {
    data(CategoryGroups);
    editClicked &&
      setSelectedId(checkData(CategoryGroups, selectedId) ? selectedId : null);
  }, [CategoryGroups]);

  const updateCategoryGroups = () => {
    if (!categoryGroupCode || String(categoryGroupCode).trim().length === 0) {
      setAlertMessage("Category group is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    if (
      !validateString(
        categoryGroupCode,
        validationLength.categoryGroup,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return;
    }
    if (
      checkDuplicate(
        CategoryGroups.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.CATEGORYGROUP,
        categoryGroupCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setCategoryGroups((prev: any) => {
      return prev.map((item: any) => {
        if (item.id === selectedId) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editCategoryGroups(null);
    setAlertMessage(`${GolfPropertyEnum.categoryGroups} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addCategoryGroups = () => {
    if (!categoryGroupCode || String(categoryGroupCode).trim().length === 0) {
      setAlertMessage("Category group is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    if (
      !validateString(
        categoryGroupCode,
        validationLength.categoryGroup,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return;
    }
    if (
      checkDuplicate(
        CategoryGroups,
        DNDFieldsEnum.CATEGORYGROUP,
        categoryGroupCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setCategoryGroups((prev: any) => {
      return updateData(
        prev?.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editCategoryGroups(null);
  };

  const editCategoryGroups = (list: any) => {
    setSelectedId(list?.id ?? null);
    setEditClicked(list != null);
    setCategoryGroupCode(list?.[DNDFieldsEnum.CATEGORYGROUP] ?? null);
    setActive(list?.Active ?? true);
    setNameError(false);
  };

  const deleteCategoryGroups = (id: number) => {
    setCategoryGroups(deleteItem(CategoryGroups, id));
  };

  const payload = {
    "Category Group": categoryGroupCode,
    Active: active,
  };

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Stack direction="row" width="100%" justifyContent="space-between">
              <Typography variant="h6" component="div" sx={textStyle}>
                Category Groups
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.categoryGroup)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              <Stack direction="row" gap="10px" justifyContent="space-between">
                {editClicked && (
                  <ClearButton
                    click={() => {
                      editCategoryGroups(null);
                      setSelectedId(null);
                    }}
                  />
                )}
                {lastUpdatedRecord && (
                  <CopyButton
                    click={() => {
                      editCategoryGroups(lastUpdatedRecord);
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Category Group
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.categoryGroup)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="CategoryGroup"
                variant="outlined"
                placeholder="Category Group"
                helperText={nameError ? validationMessages.categoryGroup : ""}
                error={nameError}
                onChange={(e: any) => {
                  setCategoryGroupCode(e.target.value);
                  setNameError(false);
                }}
                value={categoryGroupCode ?? ""}
                sx={{ width: "100%" }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            gap="50px"
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip title={tooltipComponent(golfTooltipHeaders.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateCategoryGroups() : addCategoryGroups()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Category Group
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Category Group Available</Typography>
            <Stack direction="column" gap="10px">
              {CategoryGroups ? (
                <DraggableItemList
                  itemData={CategoryGroups}
                  onOrderUpdate={(order) => setCategoryGroups(order)}
                  onEdit={(info) => editCategoryGroups(info)}
                  onDelete={(id) => deleteCategoryGroups(id)}
                  code={DNDFieldsEnum.CATEGORYGROUP}
                  name={DNDFieldsEnum.ACTIVE}
                />
              ) : (
                <Typography variant="body2">
                  No Category Group Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default CategoryGroups;
